import React, { useMemo } from 'react'
import { graphql } from 'gatsby'
import { useQuery } from '@apollo/client'
import PropTypes from 'prop-types'
import { getRegionZone } from '@helpers/geo-location'
import { styled } from '@mui/material/styles'
import BaseSwiper from '../../../../@rtg2022/components/organisms/BaseSwiper/BaseSwiper'
import StrapiHeader from '../Header'
import StrapiBanner from '../Banner'
import { GET_PRODUCTS } from '../Slider-v2'
import { getSlides, getDesktopBreakpoints, getMobileBreakpoints } from './helpers'

const FixedBannerSwiperWrapper = styled('div')(({ theme, Spacing, MobileSpacing, DisplayDesktop, DisplayMobile }) => ({
  display: DisplayDesktop === false ? 'none' : 'block',
  padding: DisplayDesktop === false ? 0 : `${Spacing?.TopPadding ?? 0}px 0 ${Spacing?.BottomPadding ?? 0}px`,
  [theme.breakpoints.down('md')]: {
    display: DisplayMobile === false ? 'none' : 'block',
    padding:
      DisplayMobile === false
        ? 0
        : `${MobileSpacing?.TopPadding ?? Spacing?.TopPadding ?? 0}px 0 ${MobileSpacing?.BottomPadding ??
            Spacing?.BottomPadding ??
            0}px`,
  },
}))

const DesktopBannerWrapper = styled('div')(({ theme, NumOfSlidesDesktop }) => ({
  width: '44%',
  position: 'relative',
  '@media (min-width: 820px)': {
    width: '30%',
  },
  '@media (min-width: 1024px)': {
    width: NumOfSlidesDesktop === 'default_3_tiles' ? '30%' : '23%',
  },
  '@media (min-width: 1280px)': {
    width: NumOfSlidesDesktop === 'default_3_tiles' ? '23%' : '18.5%',
  },
}))

const AbsoluteBannerInnerWrapper = styled('div')({
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  position: 'absolute',
  left: 0,
  top: 0,
  '& > div': {
    padding: 0,
    margin: 0,
  },
})

const DesktopSwiperWrapper = styled('div')(({ theme, NumOfSlidesDesktop }) => ({
  '& .swiper-slide:first-of-type': {
    paddingLeft: '16px',
  },
  width: '56%',
  '@media (min-width: 820px)': {
    width: '70%',
  },
  '@media (min-width: 1024px)': {
    width: NumOfSlidesDesktop === 'default_3_tiles' ? '70%' : '77%',
  },
  '@media (min-width: 1280px)': {
    width: NumOfSlidesDesktop === 'default_3_tiles' ? '77%' : '81.5%',
  },
}))

const DesktopComponentWrapper = styled('div')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
    alignItems: 'stretch',
  },
}))

const MobileVariantOneWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}))

const MobileVariantTwoWrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}))

const MobileBannerVariant1Wrapper = styled('div')({
  '& > div': {
    padding: 0,
    margin: 0,
    width: '100%',
    height: '100%',
  },
})

const MobileBannerVariant2Wrapper = styled('div')({
  position: 'relative',
  height: '100%',
  // width: '100%',
  width: '155px',
})

const FixedBannerSwiper = ({ data }) => {
  const { region, distribution_index, zone } = getRegionZone()

  const { FixedBannerSwiper: fixedBannerSwiperData } = data
  const { Header, Banner, Spacing, MobileSpacing, DisplayDesktop, DisplayMobile } = fixedBannerSwiperData
  const NumOfSlidesDesktop = fixedBannerSwiperData?.NumOfSlidesDesktop ?? 'default_3_tiles'
  const MobileVariant = fixedBannerSwiperData?.MobileVariant ?? 'variant1_banner_top_slides_bottom'

  const isProductTileSlider = fixedBannerSwiperData?.Slides?.[0]?.__typename === 'BaseProductTile'
  const productTileSkus =
    isProductTileSlider && fixedBannerSwiperData?.Slides?.map(slide => slide?.SKU)?.filter(sku => !!sku)

  const { data: productsData } = useQuery(GET_PRODUCTS, {
    variables: { skus: productTileSkus, region, priceZone: zone, distributionIndex: parseInt(distribution_index) },
    skip: !productTileSkus || productTileSkus.length < 1,
  })
  const getProducts = productsData?.getProducts

  const couponSliderSkus = useMemo(
    () => getProducts?.filter(product => product.couponDisplayData)?.map(product => product.sku),
    [getProducts],
  )

  const slides = useMemo(() => {
    let productSlides
    if (getProducts) {
      productSlides = getProducts
        ?.filter?.(product => product.showAddToCart)
        ?.map?.(product => ({
          SKU: product.sku,
          __typename: 'BaseProductTile',
        }))
    }

    return getSlides(productSlides ?? fixedBannerSwiperData?.Slides, couponSliderSkus)
  }, [couponSliderSkus, fixedBannerSwiperData.Slides, getProducts])

  const variant2Slides = useMemo(() => {
    if (MobileVariant !== 'variant2_banner_part_of_slides') return []
    let productSlides
    if (getProducts) {
      productSlides = getProducts
        ?.filter?.(product => product.showAddToCart)
        ?.map?.(product => ({
          SKU: product.sku,
          __typename: 'BaseProductTile',
        }))
    }
    const tiles = getSlides(productSlides ?? fixedBannerSwiperData?.Slides, couponSliderSkus)
    const banner = (
      <MobileBannerVariant2Wrapper>
        <AbsoluteBannerInnerWrapper>
          <StrapiBanner data={Banner} isFixedBannerSwiper />
        </AbsoluteBannerInnerWrapper>
      </MobileBannerVariant2Wrapper>
    )
    return [banner, ...tiles]
  }, [Banner, MobileVariant, couponSliderSkus, fixedBannerSwiperData.Slides, getProducts])

  const desktopBreakpoints = getDesktopBreakpoints(NumOfSlidesDesktop)
  const mobileBreakpoints = getMobileBreakpoints(isProductTileSlider)

  if (slides && slides.length === 0) return null

  return (
    <FixedBannerSwiperWrapper
      DisplayDesktop={DisplayDesktop}
      DisplayMobile={DisplayMobile}
      Spacing={Spacing}
      MobileSpacing={MobileSpacing}
    >
      <StrapiHeader data={Header} />
      <DesktopComponentWrapper>
        <DesktopBannerWrapper NumOfSlidesDesktop={NumOfSlidesDesktop}>
          <AbsoluteBannerInnerWrapper>
            <StrapiBanner data={Banner} isFixedBannerSwiper />
          </AbsoluteBannerInnerWrapper>
        </DesktopBannerWrapper>
        <DesktopSwiperWrapper NumOfSlidesDesktop={NumOfSlidesDesktop}>
          <BaseSwiper slides={slides} breakpoints={desktopBreakpoints} spaceBetween={16} />
        </DesktopSwiperWrapper>
      </DesktopComponentWrapper>
      {MobileVariant === 'variant1_banner_top_slides_bottom' && (
        <MobileVariantOneWrapper>
          <MobileBannerVariant1Wrapper>
            <StrapiBanner data={Banner} isFixedBannerSwiper />
          </MobileBannerVariant1Wrapper>
          <BaseSwiper
            slides={slides}
            breakpoints={mobileBreakpoints}
            spaceBetween={16}
            spaceBetweenMobile={8}
            navigationVariant="variant2"
            hideScrollbar
            slidesOffsetBefore={16}
          />
        </MobileVariantOneWrapper>
      )}
      {/* Todo: putting the banner in the middle of the slides */}
      {MobileVariant === 'variant2_banner_part_of_slides' && (
        <MobileVariantTwoWrapper>
          <BaseSwiper
            slides={variant2Slides}
            breakpoints={mobileBreakpoints}
            spaceBetween={16}
            spaceBetweenMobile={8}
            navigationVariant="variant2"
            hideScrollbar
            slidesOffsetBefore={16}
          />
        </MobileVariantTwoWrapper>
      )}
    </FixedBannerSwiperWrapper>
  )
}

export default FixedBannerSwiper

FixedBannerSwiper.propTypes = {
  data: PropTypes.shape({
    FixedBannerSwiper: PropTypes.object,
  }),
}

export const FixedBannerSwiperFragment = graphql`
  fragment FixedBannerSwiperFragment on StrapiFixedBannerSwiper {
    id
    testId
    Header {
      ...StrapiHeaderFragment
    }
    Banner {
      Banner {
        ...StrapiPageBannerFragment
      }
    }
    Slides {
      __typename
      ... on BaseProductTile {
        SKU
      }
      ... on BaseCategoryTile {
        CategoryTile {
          ...StrapiCategoryTileFragment
        }
      }
      ... on PageMarkdown {
        Markdown {
          ...StrapiPageMarkdownFragment
        }
      }
    }
    Spacing {
      TopPadding
      BottomPadding
    }
    MobileSpacing {
      TopPadding
      BottomPadding
    }
    DisplayDesktop
    DisplayMobile
    NumOfSlidesDesktop
    MobileVariant
  }
`
